import { useState } from 'react';
import { ApolloError } from '@apollo/client';
import { Box } from '@mui/material';

import {
  IPaginationProps,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  LoadedContent,
  Typography,
  TableContainer,
} from '@occc/ui-design-system';
import { snackbar } from '@occc-app/design-system';
import {
  RetailerNetworkUserUpdateMutation,
  RetailerNetworkFragment,
  RetailerNetworkUserFragment,
  RetailerNetworkUserUpdate,
} from '@occc/graphql';

import { RetailerNetworkUsersTableRow } from './retailer-network-users-table-row.component';
import { RetailerNetworkRemoveUserDialog } from './components/retailer-network-remove-user-dialog.component';

import {
  useRemoveUserFromRetailerNetworkMutation,
  useRetailerNetworkUserUpdateMutation,
} from '../../data-access/mutations';
import { useRetailerNetworkUserSelector } from '../retailer-network-context/use-retailer-network-user.selector';

type RetailerNetworkUsersTableProps = {
  data?: RetailerNetworkUserFragment[];
  loading: boolean;
  error?: ApolloError;
  isFilterApplied?: boolean;
  paginationProps: IPaginationProps;
  retailerNetwork: RetailerNetworkFragment;
  handleRedirectToUserDetailsPage: (userUuid: string) => void;
};

export const RetailerNetworkUsersTable = ({
  data,
  loading,
  error,
  isFilterApplied,
  paginationProps,
  retailerNetwork,
  handleRedirectToUserDetailsPage,
}: RetailerNetworkUsersTableProps) => {
  const { retailerNetworkUserUuid } = useRetailerNetworkUserSelector();
  const [userToRemove, setUserToRemove] = useState<null | RetailerNetworkUserFragment>(null);
  const [removeUserDialogOpen, setRemoveUserDialogOpen] = useState<boolean>(false);

  const [removeUserFromRetailerNetwork, { loading: isRemoveUserFromRetailerNetworkInProgress }] =
    useRemoveUserFromRetailerNetworkMutation();

  const onRetailerNetworkUserUpdateCompleted = (
    retailerNetworkUserUpdateData: RetailerNetworkUserUpdateMutation,
  ) => {
    const user = retailerNetworkUserUpdateData.retailerNetworkUserUpdate;
    snackbar.success(`${user.name} user role has been successfully changed`);
  };

  const [retailerNetworkUserUpdate, { loading: isRetailerNetworkUserUpdateInProgress }] =
    useRetailerNetworkUserUpdateMutation({
      onCompleted: onRetailerNetworkUserUpdateCompleted,
    });

  const handleOpenRemoveConfirmationDialog = (user: RetailerNetworkUserFragment) => () => {
    setUserToRemove(user);
    setRemoveUserDialogOpen(true);
  };

  const handleCloseRemoveConfirmationDialog = () => {
    setUserToRemove(null);
    setRemoveUserDialogOpen(false);
  };

  const handleUpdateRetailerNetworkUserRole = async (input: RetailerNetworkUserUpdate) => {
    const isCurrentUserUpdatingTheirOwnRole =
      input.retailerNetworkUserUuid === retailerNetworkUserUuid;
    const roleRelatedQueries = isCurrentUserUpdatingTheirOwnRole
      ? ['currentUserAbilities', 'currentUserRetailerNetworkUsers']
      : [];

    await retailerNetworkUserUpdate({
      variables: {
        input,
      },
      refetchQueries: ['retailerNetworkUsers', ...roleRelatedQueries],
    });
    setUserToRemove(null);
    setRemoveUserDialogOpen(false);
  };

  const handleRemoveUserFromRetailerNetwork = async () => {
    if (userToRemove) {
      await removeUserFromRetailerNetwork({
        refetchQueries: ['retailerNetworkUsers'],
        variables: {
          retailerNetworkUuid: retailerNetwork.uuid,
          userUuid: userToRemove?.userUuid,
        },
      });
    }
    setUserToRemove(null);
    setRemoveUserDialogOpen(false);
  };

  const renderData = (items: RetailerNetworkUserFragment[]) => {
    const rows = items.map((item) => (
      <RetailerNetworkUsersTableRow
        key={item.uuid}
        item={item}
        onRemoveItemClick={handleOpenRemoveConfirmationDialog(item)}
        handleUpdateRetailerNetworkUserRole={handleUpdateRetailerNetworkUserRole}
        isRetailerNetworkUserUpdateInProgress={isRetailerNetworkUserUpdateInProgress}
        handleRedirectToUserDetailsPage={handleRedirectToUserDetailsPage}
        retailerNetworkUuid={retailerNetwork.uuid}
      />
    ));

    return (
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Mobile Phone Number</TableCell>
              <TableCell>Role</TableCell>
              <TableCell align='right'>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>{rows}</TableBody>
        </Table>
        <Pagination {...paginationProps} />
      </TableContainer>
    );
  };

  const renderNoData = () => (
    <Box
      py={14}
      maxWidth={500}
      margin='0 auto'
      display='flex'
      alignItems='center'
      flexDirection='column'
    >
      <Typography variant='h6' align='center' paragraph>
        {isFilterApplied ? 'No Results Found' : 'Add or Create Network Users and Admins'}
      </Typography>
      <Typography align='center' color='textSecondary' paragraph>
        {isFilterApplied
          ? 'Please adjust your query and try again.'
          : 'Network admins can edit all the network details, add new sites and new users. Network users have read-only access.'}
      </Typography>
    </Box>
  );

  return (
    <>
      <LoadedContent<RetailerNetworkUserFragment>
        error={error}
        loading={loading || isRemoveUserFromRetailerNetworkInProgress}
        items={data}
        renderData={renderData}
        renderNoData={renderNoData}
        renderEmpty={renderNoData}
      />
      {removeUserDialogOpen && (
        <RetailerNetworkRemoveUserDialog
          open={removeUserDialogOpen}
          onClose={handleCloseRemoveConfirmationDialog}
          onConfirm={handleRemoveUserFromRetailerNetwork}
          user={userToRemove}
          retailerNetworkName={retailerNetwork.name}
        />
      )}
    </>
  );
};
